import { useState, useEffect } from 'react';
import { useFiltersStore } from '@/store/useFiltersStore';
import { getSurveyResponses } from '@/services/survey.service';

const FiltersPanel = () => {
  const { filters, setFilters, resetFilters: resetFiltersStore } = useFiltersStore();
  const [uniqueProducts, setUniqueProducts] = useState<string[]>([]);
  const [uniqueCompanies, setUniqueCompanies] = useState<string[]>([]);

  useEffect(() => {
    const loadUniqueValues = async () => {
      try {
        const allResponses = await getSurveyResponses({ showDeleted: false });
        const products = [
          ...new Set(allResponses.map((r) => r.details.productOrService)),
        ].sort();
        const companies = [
          ...new Set(allResponses.map((r) => r.details.companyName)),
        ].sort();
        setUniqueProducts(products);
        setUniqueCompanies(companies);
      } catch (error) {
        console.error('Failed to load filter options:', error);
      }
    };
    void loadUniqueValues();
  }, []);

  const handleDateRangeChange = (field: 'start' | 'end', value: string) => {
    const currentRange = filters.dateRange || { start: '', end: '' };
    setFilters({
      dateRange: {
        ...currentRange,
        [field]: value,
      },
    });
  };

  const handleFilterChange = (
    field: keyof typeof filters,
    value: string | boolean | undefined
  ) => {
    setFilters({ [field]: value || undefined });
  };

  const resetFilters = () => {
    resetFiltersStore();
  };

  return (
    <div className="bg-white shadow-lg rounded-xl p-6 mb-8 border border-gray-100">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="bg-primary-100 p-2 rounded-lg">
            <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900">Filters</h2>
        </div>
        <button
          onClick={resetFilters}
          className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-150"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Reset
        </button>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Start Date
          </label>
          <div className="relative">
            <input
              type="date"
              value={filters.dateRange?.start?.split('T')[0] || ''}
              onChange={(e) => handleDateRangeChange('start', e.target.value)}
              className="block w-full rounded-lg border-gray-300 bg-white shadow-sm text-gray-900 focus:border-primary-500 focus:ring-2 focus:ring-primary-500 sm:text-sm px-4 py-2.5 border transition-all duration-200"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            End Date
          </label>
          <div className="relative">
            <input
              type="date"
              value={filters.dateRange?.end?.split('T')[0] || ''}
              onChange={(e) => handleDateRangeChange('end', e.target.value)}
              className="block w-full rounded-lg border-gray-300 bg-white shadow-sm text-gray-900 focus:border-primary-500 focus:ring-2 focus:ring-primary-500 sm:text-sm px-4 py-2.5 border transition-all duration-200"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Product/Service
          </label>
          <select
            value={filters.productOrService || ''}
            onChange={(e) => handleFilterChange('productOrService', e.target.value)}
            className="block w-full rounded-lg border-gray-300 shadow-sm text-gray-900 focus:border-primary-500 focus:ring-2 focus:ring-primary-500 sm:text-sm px-4 py-2.5 border bg-white transition-all duration-200"
          >
            <option value="" className="text-gray-900">All Products</option>
            {uniqueProducts.map((product) => (
              <option key={product} value={product} className="text-gray-900">
                {product}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Company
          </label>
          <select
            value={filters.companyName || ''}
            onChange={(e) => handleFilterChange('companyName', e.target.value)}
            className="block w-full rounded-lg border-gray-300 shadow-sm text-gray-900 focus:border-primary-500 focus:ring-2 focus:ring-primary-500 sm:text-sm px-4 py-2.5 border bg-white transition-all duration-200"
          >
            <option value="" className="text-gray-900">All Companies</option>
            {uniqueCompanies.map((company) => (
              <option key={company} value={company} className="text-gray-900">
                {company}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="mt-6 pt-4 border-t border-gray-200">
        <label className="flex items-center cursor-pointer group">
          <input
            type="checkbox"
            checked={filters.showDeleted || false}
            onChange={(e) => handleFilterChange('showDeleted', e.target.checked)}
            className="h-5 w-5 text-primary-600 focus:ring-primary-500 border-gray-300 rounded transition-colors duration-150"
          />
          <span className="ml-3 text-sm font-medium text-gray-700 group-hover:text-gray-900">
            Include deleted responses
          </span>
        </label>
      </div>
    </div>
  );
};

export default FiltersPanel;

